/**
 * Admin Analytics API Client
 * Handles all API calls for the admin dashboard analytics endpoints
 */

import axios from '@/lib/api/axios-instance';
import type {
  AdminApiResponse,
  ExecutiveSummary,
  RevenueTrendPoint,
  CustomerDistributionItem,
  VerificationVolumePoint,
  TopBusiness,
  OperationsOverview,
  ActivityFeedItem,
  AdminCustomerListResponse,
  AdminCustomerRow,
  AdminCustomerDetail,
  SystemHealth,
  BankPerformanceRow,
  BankHealthSnapshot,
  ApiTrendPoint,
  SecurityAnomalies,
  MarketingAnalytics,
} from '@/src/types/admin/analytics';

const BASE = '/api/admin/analytics';

// ─── Executive Dashboard ──────────────────────────────────────────────────────

export async function getExecutiveSummary(): Promise<ExecutiveSummary> {
  const res = await axios.get<AdminApiResponse<ExecutiveSummary>>(
    `${BASE}/executive-summary`
  );
  return res.data.data;
}

export async function getRevenueTrend(months = 12): Promise<RevenueTrendPoint[]> {
  const res = await axios.get<AdminApiResponse<RevenueTrendPoint[]>>(
    `${BASE}/revenue-trend`,
    { params: { months } }
  );
  return res.data.data;
}

export async function getCustomerDistribution(): Promise<CustomerDistributionItem[]> {
  const res = await axios.get<AdminApiResponse<CustomerDistributionItem[]>>(
    `${BASE}/customer-distribution`
  );
  return res.data.data;
}

export async function getVerificationVolume(months = 12): Promise<VerificationVolumePoint[]> {
  const res = await axios.get<AdminApiResponse<VerificationVolumePoint[]>>(
    `${BASE}/verification-volume`,
    { params: { months } }
  );
  return res.data.data;
}

export async function getTopBusinesses(
  limit = 10,
  days = 30
): Promise<TopBusiness[]> {
  const res = await axios.get<AdminApiResponse<TopBusiness[]>>(
    `${BASE}/top-businesses`,
    { params: { limit, days } }
  );
  return res.data.data;
}

// ─── Operations Dashboard ─────────────────────────────────────────────────────

export async function getOperationsOverview(): Promise<OperationsOverview> {
  const res = await axios.get<AdminApiResponse<OperationsOverview>>(
    `${BASE}/operations-overview`
  );
  return res.data.data;
}

export async function getActivityFeed(limit = 20): Promise<ActivityFeedItem[]> {
  const res = await axios.get<AdminApiResponse<ActivityFeedItem[]>>(
    `${BASE}/activity-feed`,
    { params: { limit } }
  );
  return res.data.data;
}

export interface PackageFilterOption {
  value: string;
  label: string;
}

export async function getCustomerFilterOptions(): Promise<PackageFilterOption[]> {
  const res = await axios.get<{ success: boolean; data: { packages: PackageFilterOption[] } }>(
    `${BASE}/customers/filter-options`
  );
  return res.data.data.packages;
}

export interface CustomerListParams {
  page?: number;
  limit?: number;
  search?: string;
  package_code?: string;
  is_active?: boolean;
  kyc_status?: string;
}

export async function getCustomerList(
  params: CustomerListParams = {}
): Promise<AdminCustomerListResponse> {
  const res = await axios.get<{ success: boolean; data: AdminCustomerRow[]; pagination: AdminCustomerListResponse['pagination'] }>(
    `${BASE}/customers`,
    { params }
  );
  // Backend returns { data: [...rows], pagination: {...} } flat (not nested inside data)
  return {
    data: res.data.data,
    pagination: res.data.pagination,
  };
}

export async function getCustomerDetail(
  customerId: string
): Promise<AdminCustomerDetail> {
  const res = await axios.get<{ success: boolean; data: AdminCustomerDetail }>(
    `${BASE}/customers/${customerId}/detail`
  );
  return res.data.data;
}

export interface ToggleCustomerStatusResult {
  customer_id: string;
  email: string;
  is_active: boolean;
  updated_at: string;
  reason: string | null;
}

export async function toggleCustomerStatus(
  customerId: string,
  isActive: boolean,
  reason?: string
): Promise<ToggleCustomerStatusResult> {
  const res = await axios.patch<{ success: boolean; data: ToggleCustomerStatusResult }>(
    `${BASE}/customers/${customerId}/status`,
    { is_active: isActive, reason: reason ?? null }
  );
  return res.data.data;
}

export interface ToggleCreditFreezeResult {
  customer_id: string
  email: string
  credit_usage_frozen: boolean
  updated_at: string
}

export async function toggleCustomerCreditFreeze(
  customerId: string,
  freeze: boolean
): Promise<ToggleCreditFreezeResult> {
  const res = await axios.patch<{ success: boolean; data: ToggleCreditFreezeResult }>(
    `${BASE}/customers/${customerId}/credit-freeze`,
    { credit_usage_frozen: freeze }
  );
  return res.data.data;
}

// ─── Technical Dashboard ──────────────────────────────────────────────────────

export async function getSystemHealth(hours?: number): Promise<SystemHealth> {
  const res = await axios.get<AdminApiResponse<SystemHealth>>(
    `${BASE}/system-health`,
    { params: hours !== undefined ? { hours } : undefined }
  );
  return res.data.data;
}

export async function getBankPerformance(hours?: number): Promise<BankPerformanceRow[]> {
  const res = await axios.get<AdminApiResponse<BankPerformanceRow[]>>(
    `${BASE}/bank-performance`,
    { params: hours !== undefined ? { hours } : undefined }
  );
  return res.data.data;
}

export async function getApiTrend(hours?: number): Promise<ApiTrendPoint[]> {
  const res = await axios.get<AdminApiResponse<ApiTrendPoint[]>>(
    `${BASE}/api-trend`,
    { params: hours !== undefined ? { hours } : undefined }
  );
  return res.data.data;
}

/** Latest known bank health (does not call bank APIs). */
export async function getBankHealth(): Promise<BankHealthSnapshot> {
  const res = await axios.get<AdminApiResponse<BankHealthSnapshot>>(`${BASE}/bank-health`);
  return res.data.data;
}

/** Run live probes against bank APIs. Requires system:configure. */
export async function runBankHealthCheck(
  options: { banks?: string[]; force?: boolean } = {}
): Promise<BankHealthSnapshot> {
  const res = await axios.post<AdminApiResponse<BankHealthSnapshot>>(
    `${BASE}/bank-health/check`,
    options
  );
  return res.data.data;
}

export async function getSecurityAnomalies(): Promise<SecurityAnomalies> {
  const res = await axios.get<AdminApiResponse<SecurityAnomalies>>(
    `${BASE}/security-anomalies`
  );
  return res.data.data;
}

// ─── Marketing Dashboard ──────────────────────────────────────────────────────

export async function getMarketingAnalytics(): Promise<MarketingAnalytics> {
  const res = await axios.get<AdminApiResponse<MarketingAnalytics>>(
    `${BASE}/marketing`
  );
  return res.data.data;
}

// ─── API Activity Log ────────────────────────────────────────────────────────────────

export interface AdminApiActivityRow {
  id: string
  customer_id: string
  customer_name: string | null
  customer_email: string | null
  activity_code: string
  category: string
  severity: string
  title: string | null
  api_endpoint: string | null
  http_method: string
  response_time_ms: number
  status_code: number
  ip_address: string | null
  flagged: boolean
  risk_score: number
  occurred_at: string
}

export interface AdminApiActivityParams {
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: 'ASC' | 'DESC'
  customerId?: string
  category?: string
  severity?: string
  startDate?: string
  endDate?: string
  searchTerm?: string
  apiVersion?: 'v2' | 'v3'
  status?: 'success' | 'error'
  flaggedOnly?: boolean
}

export interface AdminApiActivityResponse {
  success: boolean
  data: AdminApiActivityRow[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
}

export async function getAdminApiActivity(
  params: AdminApiActivityParams = {}
): Promise<AdminApiActivityResponse> {
  const res = await axios.get<AdminApiActivityResponse>(`${BASE}/api-activity`, { params });
  return res.data;
}

// ─── Admin: Client API Logs (client_api_logs table) ─────────────────────────

export interface AdminClientApiLogRow {
  id: string
  customer_id: string
  customer_name: string | null
  customer_email: string | null
  request_id: string | null
  endpoint: string
  http_method: string
  bank_code?: string | null
  response_status: number | null
  processing_time_ms: number | null
  error_code: string | null
  error_message: string | null
  verification_id: string | null
  generation_id: string | null
  ip_address: string | null
  created_at: string
}

export interface AdminClientApiLogParams {
  page?: number
  limit?: number
  sortOrder?: 'ASC' | 'DESC'
  customerId?: string
  searchTerm?: string
  startDate?: string
  endDate?: string
  status?: 'success' | 'error'
  /** api = called with an API key, web = called from the dashboard (JWT), omit = both */
  source?: 'api' | 'web'
}

export interface AdminClientApiLogResponse {
  success: boolean
  data: AdminClientApiLogRow[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
}

export async function getAdminClientApiLogs(
  params: AdminClientApiLogParams = {}
): Promise<AdminClientApiLogResponse> {
  const res = await axios.get<AdminClientApiLogResponse>(`${BASE}/client-logs`, { params });
  return res.data;
}

// ─── Admin: Client API Log Detail ───────────────────────────────────────────

export interface AdminApiLogDetail {
  id: string
  customerId: string
  customerName: string | null
  customerEmail: string | null
  requestId: string
  correlationId: string | null
  endpoint: string
  method: string
  responseStatus: number | null
  processingTimeMs: number | null
  errorCode: string | null
  errorMessage: string | null
  verificationId: string | null
  generationId: string | null
  ipAddress: string | null
  createdAt: string
  requestHeaders: Record<string, any> | null
  requestBody: Record<string, any> | null
  responseBody: Record<string, any> | null
  bankResponse: Record<string, any> | null
}

export interface AdminApiLogDetailResponse {
  success: boolean
  data: AdminApiLogDetail
}

export async function getAdminApiLogDetail(logId: string): Promise<AdminApiLogDetailResponse> {
  const res = await axios.get<AdminApiLogDetailResponse>(`${BASE}/client-logs/${logId}`);
  return res.data;
}
